import { withMutations, withResource } from '@angular-architects/ngrx-toolkit';
import { computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { CropFieldAdvice, TreatmentType } from '@next/arable-lib/advice';
import { DialogService } from '@next/core-lib/dialog';
import { LocaleService } from '@next/core-lib/i18n';
import { createRxMutation, createRxResource } from '@next/core-lib/notification';
import { UserSelectors } from '@next/core-lib/user';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { Store } from '@ngrx/store';
import { forkJoin, map, of } from 'rxjs';

import { selectCropFieldAdvices, selectManualCropAids } from '../../../advices/store/selectors';
import { AppState, CropAid, GroupedTreatments, SprayTypes } from '../../models';
import { withCustomerParams } from '../../utils/customer-params.utils';
import { toLocaleSignal } from '../../utils/locale.utils';
import {
  buildSprayApplicationHistoryGroups,
  buildSprayCocktailOptions,
  BackendSprayType,
  CreateSprayApplicationRequest,
  mapDiseaseContextToBackendDisease,
  SprayApplicationCropField,
  SprayApplicationDialogData,
  SprayCocktail,
} from '../models/spray-application.models';
import { SprayApplicationDialogComponent } from '../components/spray-application-dialog/spray-application-dialog.component';
import { SprayApplicationService } from '../services/spray-application.service';

interface SprayApplicationState {
  diseaseContext?: SprayApplicationDialogData['diseaseContext'];
  selectedCropFields: SprayApplicationCropField[];
  applicationDate?: Date;
  sprayType?: SprayTypes;
  modifiedCropFieldIds: number[];
}

interface SprayCocktailByType {
  sprayType: SprayTypes;
  sprayCocktails: SprayCocktail[];
}

const initialState: SprayApplicationState = {
  diseaseContext: undefined,
  selectedCropFields: [],
  applicationDate: undefined,
  sprayType: undefined,
  modifiedCropFieldIds: [],
};

const sprayTypeRequests: Array<{ sprayType: SprayTypes; backendSprayType: BackendSprayType }> = [
  { sprayType: SprayTypes.PreventiveSpray, backendSprayType: 'Preventive' },
  { sprayType: SprayTypes.CurativeSpray, backendSprayType: 'Curative' },
  { sprayType: SprayTypes.StopSpray, backendSprayType: 'Stop' },
];

export const SprayApplicationSignalStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withResource(
    (store) => {
      const sprayApplicationService = inject(SprayApplicationService);
      const appStore = inject(Store<AppState>);
      const customer = appStore.selectSignal(UserSelectors.selectCustomer);

      return {
        sprayCocktails: createRxResource({
          params: () =>
            withCustomerParams(customer()?.id, (customerId) => {
              const diseaseContext = store.diseaseContext();
              const applicationDate = store.applicationDate();

              if (!diseaseContext || !applicationDate) {
                return undefined;
              }

              return {
                customerId,
                applicationDate,
                disease: mapDiseaseContextToBackendDisease(diseaseContext),
              };
            }),
          stream: ({ params }) =>
            forkJoin(
              sprayTypeRequests.map((sprayTypeRequest) =>
                sprayApplicationService.getSprayCocktails(
                  params.applicationDate,
                  params.disease,
                  sprayTypeRequest.backendSprayType,
                  params.customerId,
                ),
              ),
            ).pipe(
              map(
                (sprayCocktailsByType): SprayCocktailByType[] =>
                  sprayCocktailsByType.map((sprayCocktails, index) => ({
                    sprayType: sprayTypeRequests[index].sprayType,
                    sprayCocktails,
                  })),
              ),
            ),
          defaultValue: [],
          errorMessage: 'errors.sprayApplications.genericError',
        }),
        sprayApplicationHistory: createRxResource({
          params: () =>
            withCustomerParams(customer()?.id, (customerId) => {
              const diseaseContext = store.diseaseContext();
              const cropFieldIds = store.selectedCropFields().map((cropField) => cropField.cropFieldId);

              if (!diseaseContext || !cropFieldIds.length) {
                return undefined;
              }

              return {
                customerId,
                cropFieldIds,
                disease: mapDiseaseContextToBackendDisease(diseaseContext),
              };
            }),
          stream: ({ params }) =>
            params.cropFieldIds.length
              ? sprayApplicationService.getSprayApplications(params.cropFieldIds, params.disease, params.customerId)
              : of([]),
          defaultValue: [],
          errorMessage: 'errors.sprayApplications.genericError',
        }),
      };
    },
    {
      errorHandling: 'previous value',
    },
  ),
  withMutations((store, appStore = inject(Store<AppState>)) => {
    const sprayApplicationService = inject(SprayApplicationService);
    const customer = appStore.selectSignal(UserSelectors.selectCustomer);
    const trackModifiedCropFieldIds = (cropFieldIds: number[]): void => {
      patchState(store, {
        modifiedCropFieldIds: Array.from(new Set([...store.modifiedCropFieldIds(), ...cropFieldIds])),
      });
    };

    return {
      createSprayApplication: createRxMutation<CreateSprayApplicationRequest, void>({
        operation: (request) => sprayApplicationService.createSprayApplications(request, customer()?.id),
        onSuccess: (_, request) => {
          trackModifiedCropFieldIds(request.cropFieldIds);
          store._sprayApplicationHistoryReload();
        },
        notification: {
          successMessage: 'sprayApplications.addSuccess',
          errorMessage: 'errors.sprayApplications.genericError',
        },
      }),
      deleteSprayApplication: createRxMutation<{ cropFieldId: number; sprayApplicationId: string }, void>({
        operation: ({ cropFieldId, sprayApplicationId }) =>
          sprayApplicationService.deleteSprayApplication(cropFieldId, sprayApplicationId, customer()?.id),
        onSuccess: (_, request) => {
          trackModifiedCropFieldIds([request.cropFieldId]);
          store._sprayApplicationHistoryReload();
        },
        notification: {
          successMessage: 'sprayApplications.deleteSuccess',
          errorMessage: 'errors.sprayApplications.genericError',
        },
      }),
    };
  }),
  withComputed((store, localeService = inject(LocaleService), appStore = inject(Store<AppState>)) => {
    const customer = appStore.selectSignal(UserSelectors.selectCustomer);
    const locale = toLocaleSignal(localeService);
    const cropFieldAdvices = toSignal(appStore.select(selectCropFieldAdvices), { initialValue: [] as CropFieldAdvice[] });
    const manualCropAids = toSignal(appStore.select(selectManualCropAids), { initialValue: [] as CropAid[] });

    return {
      customer,
      availableSprayTypes: computed(() =>
        store
          .sprayCocktailsValue()
          .filter((sprayCocktailGroup) => sprayCocktailGroup.sprayCocktails.length > 0)
          .map((sprayCocktailGroup) => sprayCocktailGroup.sprayType),
      ),
      sprayCocktailOptions: computed(() => {
        const selectedSprayType = store.sprayType();
        if (!selectedSprayType) {
          return [];
        }

        const sprayCocktailGroup = store
          .sprayCocktailsValue()
          .find((groupedSprayCocktails) => groupedSprayCocktails.sprayType === selectedSprayType);

        return buildSprayCocktailOptions(sprayCocktailGroup?.sprayCocktails ?? [], locale());
      }),
      groupedSprayHistory: computed(() =>
        buildSprayApplicationHistoryGroups(store.sprayApplicationHistoryValue(), locale()),
      ),
      hasSelectedCropFields: computed(() => store.selectedCropFields().length > 0),
      groupedTreatments: computed((): GroupedTreatments[] => {
        const advices = cropFieldAdvices();
        const selectedCropFieldIds = store.selectedCropFields().map((cf) => cf.cropFieldId);
        const diseaseContext = store.diseaseContext();
        const cropAids = manualCropAids();
        const cropAidMap = new Map<number, CropAid>(cropAids.map((cropAid) => [cropAid.id, cropAid]));

        if (!advices || !selectedCropFieldIds.length || !diseaseContext) {
          return [];
        }

        const selectedCropFields = advices.filter((cropField) =>
          selectedCropFieldIds.includes(cropField.cropFieldId),
        );

        const matchesDiseaseContext = (cropAid: CropAid | undefined): boolean => {
          const cropProtection = cropAid?.cropProtection;
          switch (diseaseContext) {
            case 'blight':
              return Boolean(cropProtection?.isPhytophthora);
            case 'downy-mildew':
              return Boolean(cropProtection?.isDownyMildew);
            case 'virus-monitor':
              return Boolean(cropProtection?.isVirusMonitor);
            default:
              return false;
          }
        };

        const filterDiseaseRelevantAdvices = (treatment: (typeof selectedCropFields)[number]['treatments'][number]) => ({
          ...treatment,
          aidAdvices: treatment.aidAdvices.filter((advice) => matchesDiseaseContext(cropAidMap.get(advice.cropAidId))),
        });

        return selectedCropFields
          .map((cropField) => {
            const gbmTreatments = cropField.treatments
              .filter((treatment) => treatment.treatmentType === TreatmentType.GBM)
              .map(filterDiseaseRelevantAdvices)
              .filter((treatment) => treatment.aidAdvices.length > 0);

            return {
              cropFieldId: cropField.cropFieldId,
              cropFieldName: cropField.name,
              treatments: gbmTreatments,
            };
          })
          .filter((group) => group.treatments.length > 0);
      }),
      cropAidMap: computed(() => new Map<number, CropAid>(manualCropAids().map((cropAid) => [cropAid.id, cropAid]))),
    };
  }),
  withMethods((store, dialogService = inject(DialogService)) => ({
    openDialog(data: SprayApplicationDialogData): ReturnType<DialogService['fullscreenDialog']> {
      patchState(store, {
        diseaseContext: data.diseaseContext,
        selectedCropFields: [...data.cropFields],
        applicationDate: data.defaultDate ?? new Date(),
        sprayType: data.initialSprayType,
        modifiedCropFieldIds: [],
      });

      return dialogService.fullscreenDialog(SprayApplicationDialogComponent, data, { maxWidth: '80vw' });
    },
    setApplicationDate(applicationDate: Date): void {
      patchState(store, { applicationDate });
    },
    setSprayType(sprayType: SprayTypes): void {
      patchState(store, { sprayType });
    },
    removeCropField(cropFieldId: number): void {
      patchState(store, {
        selectedCropFields: store.selectedCropFields().filter((cropField) => cropField.cropFieldId !== cropFieldId),
      });
    },
    createSprayRegistration(sprayId: number): void {
      const applicationDate = store.applicationDate();
      const diseaseContext = store.diseaseContext();
      const cropFieldIds = store.selectedCropFields().map((cropField) => cropField.cropFieldId);

      if (!applicationDate || !diseaseContext || !cropFieldIds.length) {
        return;
      }

      store.createSprayApplication({
        cropFieldIds,
        applicationDate,
        sprayId,
        disease: mapDiseaseContextToBackendDisease(diseaseContext),
      });
    },
    deleteSprayRegistration(cropFieldId: number, sprayApplicationId: string): void {
      store.deleteSprayApplication({ cropFieldId, sprayApplicationId });
    },
  })),
);
